<script lang="ts">
export type PageHeaderContentReadMore = {
  text: string;
  href: string;
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { FunctionalComponent } from 'vue';
import { RouterLink } from 'vue-router';
import * as LucideIcons from 'lucide-vue-next';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { buildSrcSet, isOptimisedImage } from '@/types/images';
import type { ApiImage } from '@/types/images';
import { wagtailRichTitleInnerHtmlForH1 } from '@/utils/html';
import { offlineMediaPlaceholderUrl } from '@/utils/offlineMediaPlaceholder';

const ArrowRight = (LucideIcons as Record<string, FunctionalComponent>).ArrowRight;

/**
 * NormalPage "content" header: prototype PageHero layout (full-bleed gradient band,
 * two-column title + image on large screens). Distinct from HomePage featured proportions.
 */
const props = defineProps<{
  plainTitle: string;
  titleHtml?: string | null;
  description?: string | null;
  image?: ApiImage | null;
  imageAlt?: string;
  readMore?: PageHeaderContentReadMore | null;
}>();

const hasTitleHtml = computed(
  () => typeof props.titleHtml === 'string' && props.titleHtml.trim().length > 0
);

const richTitleHeadingInnerHtml = computed(() => {
  const raw = props.titleHtml;
  if (typeof raw !== 'string' || !raw.trim()) return '';
  return wagtailRichTitleInnerHtmlForH1(raw);
});

const pageImage = computed(() => props.image ?? null);

const pageImageUrl = computed(() => {
  const img = pageImage.value;
  if (!img) return null;
  if (isOptimisedImage(img)) return img.fallback?.url ?? null;
  return 'url' in img ? (img as { url?: string }).url ?? null : null;
});

const hasPageImage = computed(() => {
  const img = pageImage.value;
  if (!img) return false;
  if (isOptimisedImage(img)) return !!(img.fallback?.url);
  return !!(img && 'url' in img && (img as { url?: string }).url);
});

const resolvedImageAlt = computed(() => props.imageAlt ?? props.plainTitle);

/** Like PageHeader featured band — only replace with placeholder after real load failure. */
const contentHeroMediaBroken = ref(false);

watch(
  () => props.image,
  () => {
    contentHeroMediaBroken.value = false;
  }
);

function onContentHeroMediaError() {
  contentHeroMediaBroken.value = true;
}

const richTitleHeadingEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(richTitleHeadingEl, () => richTitleHeadingInnerHtml.value);

function isExternalOrHash(href: string): boolean {
  return (
    href.startsWith('#') ||
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:')
  );
}
</script>

<template>
  <!-- Prototype PageHero: full-bleed gradient strip; see Prototype/src/app/components/PageHero.tsx -->
  <div class="page-header-content-breakout">
    <div class="page-header-content__shell">
      <div class="page-header-content__inner">
        <div
          class="page-header-content__grid"
          :class="{ 'page-header-content__grid--no-image': !hasPageImage }"
        >
          <div class="page-header-content__text">
            <h1
              v-if="hasTitleHtml"
              ref="richTitleHeadingEl"
              class="page-header-content__title page-header-content__title--rich block-rich-text"
              v-html="richTitleHeadingInnerHtml"
            />
            <h1 v-else class="page-header-content__title">{{ plainTitle }}</h1>
            <p v-if="description" class="page-header-content__description">{{ description }}</p>
            <a
              v-if="readMore && isExternalOrHash(readMore.href)"
              :href="readMore.href"
              class="page-header-content__read-more"
            >
              {{ readMore.text }}
              <ArrowRight class="page-header-content__read-more-arrow" aria-hidden="true" />
            </a>
            <RouterLink v-else-if="readMore" :to="readMore.href" class="page-header-content__read-more">
              {{ readMore.text }}
              <ArrowRight class="page-header-content__read-more-arrow" aria-hidden="true" />
            </RouterLink>
          </div>

          <div v-if="hasPageImage" class="page-header-content__image-wrap">
            <div class="page-header-content__image-overlay" aria-hidden="true" />
            <img
              v-if="contentHeroMediaBroken"
              :src="offlineMediaPlaceholderUrl()"
              :alt="resolvedImageAlt"
              class="page-header-content__img"
              decoding="async"
              width="120"
              height="80"
            />
            <picture
              v-else-if="pageImage && isOptimisedImage(pageImage)"
              class="page-header-content__picture"
            >
              <source
                v-for="source in pageImage.sources"
                :key="source.media_type"
                :type="source.media_type"
                :srcset="buildSrcSet(source.src_set)"
              />
              <img
                :src="pageImage.fallback!.url"
                :srcset="buildSrcSet(pageImage.fallback!.src_set)"
                sizes="(min-width: 1024px) 50vw, 100vw"
                :alt="resolvedImageAlt"
                class="page-header-content__img"
                fetchpriority="high"
                decoding="async"
                @error="onContentHeroMediaError"
              />
            </picture>
            <img
              v-else-if="pageImageUrl"
              :src="pageImageUrl"
              :alt="resolvedImageAlt"
              class="page-header-content__img"
              fetchpriority="high"
              decoding="async"
              @error="onContentHeroMediaError"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Full-bleed: same technique as .page-header-featured-breakout (PageHeader.vue). */
.page-header-content-breakout {
  margin-top: 0;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.page-header-content__shell {
  background: linear-gradient(
    to bottom right,
    rgba(4, 120, 87, 0.12),
    #ffffff,
    rgba(4, 120, 87, 0.18)
  );
  overflow: hidden;
  border-bottom: 1px solid var(--color-border);
}

.page-header-content__inner {
  width: 100%;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  box-sizing: border-box;
}
@media (min-width: 640px) {
  .page-header-content__inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .page-header-content__inner {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

.page-header-content__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
}
@media (min-width: 1024px) {
  .page-header-content__grid:not(.page-header-content__grid--no-image) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.page-header-content__text {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 1.5rem;
  padding-bottom: 0.5rem;
}
@media (min-width: 640px) {
  .page-header-content__text {
    padding-top: 2.5rem;
    padding-bottom: 1rem;
  }
}
@media (min-width: 768px) {
  .page-header-content__text {
    padding-top: 3rem;
    padding-bottom: 1.5rem;
  }
}
@media (min-width: 1024px) {
  .page-header-content__text {
    padding-top: 4rem;
    padding-bottom: 2rem;
  }
}

/* PageHero h1: bold; lg target 2.55rem (prototype). Mid breakpoints avoid sm:text-xl dip. */
.page-header-content__title {
  margin: 0 0 0.5rem;
  color: var(--color-text);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
}
@media (min-width: 640px) {
  .page-header-content__title {
    font-size: 1.875rem;
  }
}
@media (min-width: 768px) {
  .page-header-content__title {
    font-size: 2.25rem;
  }
}
@media (min-width: 1024px) {
  .page-header-content__title {
    font-size: 2.55rem;
    margin-bottom: 0.5rem;
  }
}

.page-header-content__title--rich.block-rich-text :deep(.indmo-richtext-primary) {
  color: var(--color-primary);
}

.page-header-content__title--rich.block-rich-text :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.15em;
  color: var(--color-primary);
}

.page-header-content__title--rich.block-rich-text :deep(.indmo-richtext-icon svg) {
  width: 1em;
  height: 1em;
}

.page-header-content__title--rich.block-rich-text :deep(p) {
  margin: 0;
}

/* Subtitle: prototype ~1.05rem → sm text-sm → md base → lg lg */
.page-header-content__description {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-sans);
  font-size: 1.05rem;
  font-weight: 400;
  line-height: 1.5;
}
@media (min-width: 640px) {
  .page-header-content__description {
    font-size: 0.875rem;
  }
}
@media (min-width: 768px) {
  .page-header-content__description {
    font-size: 1rem;
  }
}
@media (min-width: 1024px) {
  .page-header-content__description {
    font-size: 1.125rem;
  }
}

.page-header-content__read-more {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0.75rem 2rem;
  background: var(--color-button);
  color: var(--color-button-foreground);
  border-radius: var(--radius-button);
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.25;
  text-decoration: none;
  box-shadow: var(--shadow-button);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.page-header-content__read-more:hover {
  background: color-mix(in srgb, var(--color-button) 90%, transparent);
  box-shadow: var(--shadow-button-hover);
  transform: translateY(-2px);
}

.page-header-content__read-more-arrow {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.page-header-content__read-more:hover .page-header-content__read-more-arrow {
  transform: translateX(4px);
}

/* PageHero image column: h-32 sm:h-40 md:h-48 lg:h-[280px], bleed on small screens */
.page-header-content__image-wrap {
  position: relative;
  margin-left: -1rem;
  margin-right: -1rem;
  width: calc(100% + 2rem);
  height: 8rem;
  overflow: hidden;
}
@media (min-width: 640px) {
  .page-header-content__image-wrap {
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    width: calc(100% + 3rem);
    height: 10rem;
  }
}
@media (min-width: 768px) {
  .page-header-content__image-wrap {
    height: 12rem;
  }
}
@media (min-width: 1024px) {
  .page-header-content__image-wrap {
    margin-left: 0;
    margin-right: calc(-1 * max(0px, (100vw - 80rem) / 2 + 2rem));
    width: auto;
    height: 280px;
    min-height: 280px;
  }
}

.page-header-content__image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom right, rgba(4, 120, 87, 0.3), transparent);
  z-index: 10;
  pointer-events: none;
}

.page-header-content__picture,
.page-header-content__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  display: block;
}
</style>
